import type { SolutionCategory } from "@/api/solutionApi";
import { CASE_SOLUTION_MAP } from "@/utils/solutionDisplay";
import { selectRelatedCases } from "@/utils/solutionList";
import { DynamicCasesRow, SubSceneGridRow, type SubScene } from "../components/SolutionsShared";
import AiHero from "./components/AiHero";
import AiWhy from "./components/AiWhy";
import AiPositioning from "./components/AiPositioning";
import AiArchitecture from "./components/AiArchitecture";
import AiGates from "./components/AiGates";
import AiMatrix from "./components/AiMatrix";
import AiValues from "./components/AiValues";
import AiRoadmap from "./components/AiRoadmap";
import AiService from "./components/AiService";
import AiCta from "./components/AiCta";

/**
 * AI 业务栏目首页（客户确认稿 docs/ai-data-security-solution.html 的落地实现）。
 *
 * 与通用 CategoryPanel 的关系：
 * - 页面骨架/文案为设计稿专属（一期常量化于 content.ts，二期可迁移为接口数据）；
 * - 后台若后续在 ai-business 分类下录入子方案，会在 Hero 下方以子方案卡片网格展示；
 * - 关联案例继续走 /api/front/cases?solution=ai-business（无案例时该行整体隐藏）。
 *
 * 面板内容按 1200px 版心呈现（设计稿 maxw），外层页面容器为 1400px。
 */
const AI_PANEL_ACCENT = "#1a56db";

interface AiBusinessPanelProps {
  category: SolutionCategory;
  apiCases: any[];
  onNavigate: (id: string) => void;
}

export default function AiBusinessPanel({ category, apiCases, onNavigate }: AiBusinessPanelProps) {
  const subs: SubScene[] = [...(category.subSolutions || [])]
    .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
    .map((sub) => ({
      id: String(sub.id),
      icon: (sub as any).icon || "",
      name: sub.name || "",
      desc: sub.desc || "",
      tags: ((sub as any).tags && (sub as any).tags.length > 0)
        ? (sub as any).tags.filter(Boolean)
        : (sub.features || []).map((f: any) => f.name).filter(Boolean),
      productCategory: String(sub.id),
      image: sub.image || "",
    }));

  const relatedCases = selectRelatedCases(apiCases, CASE_SOLUTION_MAP["ai-business"] ?? []);

  return (
    <div className="max-w-[1200px] mx-auto">
      <AiHero />

      {/* 后台录入的 AI 子方案（当前无数据时不渲染） */}
      {subs.length > 0 && <SubSceneGridRow scenes={subs} accentColor={AI_PANEL_ACCENT} onNavigate={onNavigate} />}

      <AiWhy />
      <AiPositioning />
      <AiArchitecture />
      <AiGates />
      <AiMatrix />
      <AiValues />
      <AiRoadmap />
      <AiService />

      {/* AI 业务关联案例（/api/front/cases?solution=ai-business，无案例时整行隐藏） */}
      <DynamicCasesRow cases={relatedCases} accentColor={AI_PANEL_ACCENT} categoryId="ai-business" />

      <AiCta />
    </div>
  );
}
